'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { HStack, VStack, Box } from '@terra/ui-styled-system';
import { Text } from '../../../../../components/styled/text';
import {
  Select,
  createSelectListCollection,
} from '../../../../../components/select';
import { BadgeHttp } from '../../../../../components/styled/badge-http';
import { useTranslations } from 'next-intl';
import type { SearchHarEntryResult } from '../../../finding-search';
import { MetaDefault, MonoSmall } from '../../../../../components/typography';

export type HarEntrySelectorProps = {
  endpointId?: string;
  value?: string;
  onChange: (harEntryId: string | undefined) => void;
  onGetHarEntriesByEndpoint?: (
    endpointId: string,
  ) => Promise<SearchHarEntryResult[]>;
  disabled?: boolean;
  currentHarEntryPlaceholder?: string;
};

const formatHarEntryLabel = (entry: SearchHarEntryResult): string => {
  const method = entry.method?.toUpperCase() ?? 'HTTP';
  const url = entry.url ?? entry.id;
  const status =
    entry.status !== undefined && entry.status !== null
      ? ` (${entry.status})`
      : '';

  return `${method} ${url}${status}`;
};

export function HarEntrySelector({
  endpointId,
  value,
  onChange,
  onGetHarEntriesByEndpoint,
  disabled,
  currentHarEntryPlaceholder,
}: HarEntrySelectorProps) {
  const t = useTranslations('designSystem.chat.updateSignalModal');
  const [harEntries, setHarEntries] = useState<SearchHarEntryResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!endpointId || !onGetHarEntriesByEndpoint) {
      setHarEntries([]);

      return;
    }

    let cancelled = false;

    setIsLoading(true);

    Promise.resolve(onGetHarEntriesByEndpoint(endpointId))
      .then((entries) => {
        if (cancelled) return;
        setHarEntries(entries);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error('Failed to load HAR entries for endpoint', error);
        setHarEntries([]);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [endpointId, onGetHarEntriesByEndpoint]);

  const selectedEntry = useMemo(
    () => harEntries.find((entry) => entry.id === value),
    [harEntries, value],
  );

  const collection = useMemo(
    () =>
      createSelectListCollection({
        items: harEntries.map((entry) => ({
          label: formatHarEntryLabel(entry),
          value: entry.id,
          method: entry.method,
          url: entry.url,
          status: entry.status,
        })),
      }),
    [harEntries],
  );

  const handleValueChange = useCallback(
    (details: { value: string[] }) => {
      const nextValue = details.value[0];

      onChange(nextValue || undefined);
    },
    [onChange],
  );

  if (disabled) {
    const displayLabel =
      selectedEntry !== undefined
        ? formatHarEntryLabel(selectedEntry)
        : value
          ? (currentHarEntryPlaceholder ?? value)
          : undefined;

    return (
      <Box
        p={2.5}
        borderWidth="1px"
        borderColor="border.default"
        borderRadius="md"
        bg="bg.subtle"
      >
        {displayLabel ? (
          <VStack alignItems="stretch" gap={1} minWidth={0}>
            <HStack gap={2} alignItems="center" minWidth={0}>
              {selectedEntry?.method ? (
                <BadgeHttp
                  method={selectedEntry.method}
                  size="sm"
                  variant="emphasis"
                  flexShrink={0}
                  fontWeight="normal"
                >
                  {selectedEntry.method}
                </BadgeHttp>
              ) : null}
              <MonoSmall truncate minWidth={0}>
                {displayLabel}
              </MonoSmall>
            </HStack>
            {value ? (
              <MonoSmall color="fg.muted" truncate>
                {value}
              </MonoSmall>
            ) : null}
          </VStack>
        ) : (
          <MetaDefault color="fg.muted">{t('harEntryNotLinked')}</MetaDefault>
        )}
      </Box>
    );
  }

  if (!endpointId) {
    return (
      <Box
        p={2.5}
        borderWidth="1px"
        borderColor="border.default"
        borderRadius="md"
        bg="bg.subtle"
      >
        <MetaDefault color="fg.muted">{t('selectEndpointForHar')}</MetaDefault>
      </Box>
    );
  }

  const placeholder = isLoading
    ? t('loadingHarEntries')
    : (currentHarEntryPlaceholder ??
      selectedEntry?.id ??
      value ??
      t('placeholders.harEntryId'));

  return (
    <Select.Root
      positioning={{ sameWidth: true, strategy: 'fixed' }}
      size="sm"
      collection={collection}
      value={value ? [value] : []}
      onValueChange={handleValueChange}
      disabled={isLoading}
    >
      <Select.Control>
        <Select.Trigger>
          <Select.ValueText placeholder={placeholder} />
        </Select.Trigger>
      </Select.Control>
      <Select.Positioner>
        <Select.Content maxH="280px" overflowY="auto">
          <Select.ItemGroup>
            {collection.items.length > 0 ? (
              collection.items.map((item) => (
                <Select.Item key={item.value} item={item}>
                  <VStack alignItems="stretch" gap={0.5} flex={1} minW={0}>
                    <HStack gap={2} alignItems="center" minW={0}>
                      {item.method ? (
                        <BadgeHttp
                          method={item.method}
                          size="sm"
                          variant="emphasis"
                          flexShrink={0}
                          fontWeight="normal"
                        >
                          {item.method}
                        </BadgeHttp>
                      ) : null}
                      <Select.ItemText truncate flex={1}>
                        {item.url ?? item.label}
                      </Select.ItemText>
                      {item.status !== undefined && item.status !== null ? (
                        <BadgeHttp
                          status={item.status}
                          size="sm"
                          variant="emphasis"
                          flexShrink={0}
                        >
                          {item.status}
                        </BadgeHttp>
                      ) : null}
                    </HStack>
                    <MonoSmall color="fg.muted" truncate>
                      {item.value}
                    </MonoSmall>
                  </VStack>
                </Select.Item>
              ))
            ) : (
              <Box px={3} py={2}>
                <Text fontSize="xs" color="fg.muted" textAlign="center">
                  {isLoading ? t('loadingHarEntries') : t('noHarEntriesFound')}
                </Text>
              </Box>
            )}
          </Select.ItemGroup>
        </Select.Content>
      </Select.Positioner>
    </Select.Root>
  );
}
